/**
 * Video Thumbnail Utility
 * Fetches thumbnail and title for YouTube and Vimeo videos
 */

interface VideoMetadata {
  thumbnail: string
  title: string
}

// Extract YouTube video ID from various URL formats
export const extractYouTubeVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

// Extract Vimeo video ID from URL
export const extractVimeoVideoId = (url: string): string | null => {
  const patterns = [/vimeo\.com\/(\d+)/, /vimeo\.com\/video\/(\d+)/]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match?.[1]) return match[1]
  }
  return null
}

// Check if URL is a valid YouTube URL
export const isYouTubeUrl = (url: string): boolean => {
  return extractYouTubeVideoId(url) !== null
}

// Check if URL is a valid Vimeo URL
export const isVimeoUrl = (url: string): boolean => {
  return extractVimeoVideoId(url) !== null
}

// Check if URL is a valid video URL (YouTube or Vimeo)
export const isValidVideoUrl = (url: string): boolean => {
  if (!url) return false
  return isYouTubeUrl(url) || isVimeoUrl(url)
}

// Fetch YouTube video metadata using oEmbed API
const fetchYouTubeMetadata = async (videoId: string): Promise<VideoMetadata> => {
  try {
    const response = await fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`,
    )
    const data = await response.json()
    return {
      thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      title: data.title || "",
    }
  } catch {
    return {
      thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      title: "",
    }
  }
}

// Fetch Vimeo video metadata using oEmbed API
const fetchVimeoMetadata = async (url: string): Promise<VideoMetadata> => {
  try {
    const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`)
    const data = await response.json()
    return {
      thumbnail: data.thumbnail_url || "",
      title: data.title || "",
    }
  } catch {
    return {
      thumbnail: "",
      title: "",
    }
  }
}

// Main function to fetch video metadata (thumbnail + title)
export const fetchVideoMetadata = async (url: string): Promise<VideoMetadata> => {
  if (!url) return { thumbnail: "", title: "" }

  // YouTube
  const youtubeId = extractYouTubeVideoId(url)
  if (youtubeId) {
    return await fetchYouTubeMetadata(youtubeId)
  }

  // Vimeo
  const vimeoId = extractVimeoVideoId(url)
  if (vimeoId) {
    return await fetchVimeoMetadata(url)
  }

  return { thumbnail: "", title: "" }
}
